// Admin-only view: user management and read-only session browsing.

import { useEffect, useState } from 'react';
import { admin, type AdminUser } from '../lib/api';
import { useStore } from '../state/store';

export function AdminPage() {
  return (
    <div className="admin">
      <h1>Admin</h1>
      <Users />
      <AllSessions />
    </div>
  );
}

function Users() {
  const toast = useStore((s) => s.toast);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [draft, setDraft] = useState<{ email: string; displayName: string; role: 'admin' | 'user' }>({
    email: '', displayName: '', role: 'user',
  });
  const [issued, setIssued] = useState<{ email: string; tempPassword: string } | null>(null);

  async function refresh() {
    const { users } = await admin.listUsers();
    setUsers(users);
  }
  useEffect(() => { void refresh(); }, []);

  async function create() {
    if (!draft.email.trim() || !draft.displayName.trim()) return;
    try {
      const { user, tempPassword } = await admin.createUser(draft);
      setIssued({ email: user.email, tempPassword });
      setDraft({ email: '', displayName: '', role: 'user' });
      await refresh();
    } catch (err) {
      toast('error', `Could not create: ${(err as Error).message}`);
    }
  }

  async function reset(user: AdminUser) {
    if (!confirm(`Reset password for ${user.email}? They'll be forced to change it on next login.`)) return;
    try {
      const { tempPassword } = await admin.resetPassword(user.id);
      setIssued({ email: user.email, tempPassword });
    } catch (err) {
      toast('error', `Could not reset: ${(err as Error).message}`);
    }
  }

  async function toggleDisabled(user: AdminUser) {
    try {
      await admin.setDisabled(user.id, !user.disabledAt);
      await refresh();
    } catch (err) {
      toast('error', `Could not update: ${(err as Error).message}`);
    }
  }

  async function setRole(user: AdminUser, role: 'admin' | 'user') {
    if (role === user.role) return;
    try {
      await admin.setRole(user.id, role);
      await refresh();
    } catch (err) {
      toast('error', `Could not update: ${(err as Error).message}`);
    }
  }

  return (
    <section>
      <h2>Users</h2>
      {issued && (
        <div className="temp-password">
          <strong>Temporary password for {issued.email}:</strong>
          <code>{issued.tempPassword}</code>
          <span className="muted"> — give to the user; they must change it on first login. This is shown once.</span>
          <button onClick={() => setIssued(null)}>Dismiss</button>
        </div>
      )}
      <table className="admin-table">
        <thead>
          <tr><th>Email</th><th>Display name</th><th>Role</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          {users === null && <tr><td colSpan={5} className="muted">Loading…</td></tr>}
          {users && users.length === 0 && <tr><td colSpan={5} className="muted">No users yet.</td></tr>}
          {users?.map((u) => (
            <tr key={u.id}>
              <td>{u.email}</td>
              <td>{u.displayName}</td>
              <td>
                <select value={u.role} onChange={(e) => void setRole(u, e.target.value as 'admin' | 'user')}>
                  <option value="user">user</option>
                  <option value="admin">admin</option>
                </select>
              </td>
              <td>{u.disabledAt ? 'disabled' : 'active'}</td>
              <td>
                <button onClick={() => void reset(u)}>Reset password</button>
                <button onClick={() => void toggleDisabled(u)}>{u.disabledAt ? 'Re-enable' : 'Disable'}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>Invite a new user</h3>
      <div className="invite-row">
        <input placeholder="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
        <input placeholder="display name" value={draft.displayName} onChange={(e) => setDraft({ ...draft, displayName: e.target.value })} />
        <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value as 'admin' | 'user' })}>
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
        <button onClick={() => void create()}>Create</button>
      </div>
    </section>
  );
}

function AllSessions() {
  const [sessions, setSessions] = useState<Awaited<ReturnType<typeof admin.listAllSessions>>['sessions'] | null>(null);
  const [users, setUsers] = useState<AdminUser[] | null>(null);

  useEffect(() => {
    void admin.listAllSessions().then((r) => setSessions(r.sessions));
    void admin.listUsers().then((r) => setUsers(r.users));
  }, []);

  const userEmail = (id: string | null): string => {
    if (!id) return '—';
    return users?.find((u) => u.id === id)?.email ?? id.slice(0, 8);
  };

  return (
    <section>
      <h2>All sessions (read-only)</h2>
      <p className="muted">Admins can browse every user's sessions for QA. Editing is reserved to the owner.</p>
      <table className="admin-table">
        <thead>
          <tr><th>User</th><th>Session</th><th>Rows</th><th>Updated</th></tr>
        </thead>
        <tbody>
          {sessions === null && <tr><td colSpan={4} className="muted">Loading…</td></tr>}
          {sessions?.map((s) => (
            <tr key={s.id}>
              <td>{userEmail(s.userId)}</td>
              <td>{s.name}</td>
              <td>{s.rowCount}</td>
              <td>{new Date(s.updatedAt).toLocaleString('en-GB')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
